import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MeetingStatusApiService } from '../../../services/meeting-status-api.service';
import {
  MeetingStatusRequest,
  MeetingStatusResponse,
} from '../../../models/meeting.model';

@Component({
  selector: 'app-meeting-statuses-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './meeting-statuses-form.component.html',
})
export class MeetingStatusesFormComponent implements OnInit {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<MeetingStatusResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  statusId: number | null = null;
  currentIsSystem = false;
  private originalName = '';

  constructor(
    private fb: FormBuilder,
    private api: MeetingStatusApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // En drawer mode el wrapper invoca reset()/loadStatus() al abrir.
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      label: ['', [Validators.required, Validators.maxLength(150)]],
      description: ['', [Validators.maxLength(500)]],
      color: ['#3b82f6'],
      sortOrder: [0],
      isFinal: [false],
      isDefault: [false],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.statusId = null;
    this.currentIsSystem = false;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadStatus(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.statusId = id;
    this.api.getById(id, this.businessId).subscribe({
      next: (item) => {
        this.currentIsSystem = !!item.isSystem;
        this.form.patchValue({
          name: item.name,
          label: item.label,
          description: item.description || '',
          color: item.color || '#3b82f6',
          sortOrder: item.sortOrder ?? 0,
          isFinal: !!item.isFinal,
          isDefault: !!item.isDefault,
          isActive: item.isActive ?? true,
        });
        this.originalName = item.label;
        if (this.currentIsSystem) {
          this.form.get('name')?.disable();
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el estado';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.getRawValue();
    const body: MeetingStatusRequest = {
      businessId: this.businessId,
      name: v.name.trim(),
      label: v.label.trim(),
      description: v.description?.trim() || undefined,
      color: v.color || undefined,
      sortOrder: v.sortOrder ?? 0,
      isFinal: !!v.isFinal,
      isDefault: !!v.isDefault,
      isSystem: this.currentIsSystem,
      isActive: !!v.isActive,
    };

    const req$ = this.isEditing && this.statusId
      ? this.api.update(this.statusId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Estado de reunión',
            this.originalName !== v.label.trim()
              ? `Se actualizó "${this.originalName}" → "${v.label.trim()}".`
              : `Se actualizó "${v.label.trim()}" correctamente.`);
        } else {
          this.toast.created('Estado de reunión', `Se creó "${v.label.trim()}" correctamente.`);
        }
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
